import { useEffect, useState } from "react";

import { Toast } from "@newsela/angelou";
import { get } from "@s-libs/micro-dash";
import { useRouter } from "next/router";

import { ApiCatalog } from "@/api";
import useAdminCanMakeChanges from "@/admin/hooks/useAdminCanMakeChanges";
import useAdminConsoleAddSchool from "@/admin/hooks/useAdminConsoleAddSchool";
import { DirectoryType } from "@/admin/types";
import useDirectoryPageAuxiliaryDataContext from "@/admin/useDirectoryPageAuxiliaryDataContext";
import globalToasterRef from "@/base/globalToasterRef";
import AdminConsoleDirectoryTable from "@/directory/AdminConsoleDirectoryTable";
import {
  Cell,
  CellLink,
  CellLinkList,
  CellLinkListMore,
  CellExpiration,
  CellList,
} from "@/directory/DirectoryTable/Cells";
import { NO_SCHOOL_RECORD_FOUND } from "@/directory/DirectoryTable/NotFound";
import useGetDirectoryQuery from "@/directory/hooks/useGetDirectoryQuery";
import ADMIN_CONSOLE_ROUTE_CONTENTS from "../constants/AdminConsoleRouteContents";
import AdminConsoleBasePage from "../AdminConsoleBasePage";
import AdminConsoleDistrictAddSchoolModal from "./AdminConsoleDistrictAddSchoolModal";

const PAGE_SIZE = 30;

const SCHOOL_PROFILE_CREATE_ERROR_TOAST_PROPS = {
  showIcon: true,
  statusColor: Toast.statusColor.danger,
  children: "School creation failed",
};

const SCHOOL_PROFILE_UPDATE_SUCCESS_TOAST_PROPS = {
  showIcon: true,
  statusColor: Toast.statusColor.success,
  children: "Profile updated",
};

const transform = (data = { count: 0, results: [] }) => {
  const { count, results } = data;

  return {
    count,
    schools: results?.map((school) => {
      const {
        unique_id: uniqueId,
        name,
        districts,
        licenses,
        soonest_expiration_date: soonestExpirationDate,
        classrooms,
        sources,
      } = school;

      return {
        id: uniqueId,
        name,
        district: districts,
        licenses: licenses?.map((license) => license.product),
        expiration: soonestExpirationDate,
        classrooms: classrooms?.map((classroom) => classroom.name),
        source: get(sources, [0, "provider"], null) || "Newsela",
      };
    }),
  };
};

const COLUMNS = [
  {
    accessor: "name",
    Header: "Name",
    Cell: (cell) => {
      const schoolId = cell?.row?.original?.id;
      const schoolDetailUrl = `./${schoolId}`;
      return <CellLink value={cell.value} href={schoolDetailUrl} />;
    },
  },
  {
    accessor: "district",
    Header: "Parent district",
    Cell: (cell) => {
      return (
        <CellList
          values={cell.value?.map((district) => district.name)}
          label="district"
        />
      );
    },
  },
  {
    accessor: "licenses",
    Header: "Subscriptions",
    disableSortBy: true,
    Cell: (cell) => {
      const displayValue = cell.value?.map((item, index) =>
        index === 0 && item?.length > 30 ? `${item.substring(0, 30)}...` : item,
      );

      return (
        <CellLinkListMore
          value={displayValue}
          tooltipValue={cell.value}
          showTooltip
        />
      );
    },
  },
  {
    accessor: "expiration",
    Header: "Next expiration",
    disableSortBy: true,
    Cell: (cell) => {
      return <CellExpiration days={cell.value} limit={30} />;
    },
  },
  {
    accessor: "classrooms",
    Header: "Classes",
    disableSortBy: true,
    Cell: (cell) => {
      const schoolId = cell?.row?.original?.id;
      return (
        <CellLinkList
          value={cell.value}
          label="classes"
          href={`/admin/schools/${schoolId}/?tab=classes`}
        />
      );
    },
  },
  {
    accessor: "source",
    Header: "Data source",
    disableSortBy: true,
    Cell: (cell) => <Cell cell={cell} />,
  },
];

const AdminConsoleSchoolsDirectoryPage = () => {
  const router = useRouter();

  const [tableControls, setTableControls] = useState();
  const [tableProps, setTableProps] = useState();

  // Initialize items needed to ping the APIs that will source data for the directory pages.
  const {
    userData,
    userDataIsLoading,
    userDataFetchFailed,
    refetchStatusToGenerateCsv,
    responseFromStatusApi,
    refetchingStatusApiResponse,
  } = useDirectoryPageAuxiliaryDataContext();

  const {
    data,
    isLoading,
    refetch,
    isRefetching,
    isError,
    isFetchedAfterMount,
  } = useGetDirectoryQuery({
    user: userData,
    enabled: Boolean(userData) && !userDataIsLoading,
    controls: tableControls,
    directoryType: DirectoryType.SCHOOLS,
    rawTableProps: tableProps,
    apiCatalogEndpoint: ApiCatalog.adminConsoleDirectory.endpoint,
    apiCatalogKey: ApiCatalog.adminConsoleDirectory.key,
  });

  const [loadingState, setLoadingState] = useState(true);

  useEffect(() => {
    // Stop the loading state once we've got everything we need.
    if (!isLoading && !isRefetching && !userDataIsLoading && userData) {
      setLoadingState(false);
    }
  }, [isLoading, isRefetching, userDataIsLoading]);

  useEffect(() => {
    if (tableControls) {
      setTimeout(() => {
        setDisplayErrorMessage(false);
        setLoadingState(true);
        refetch();
        refetchStatusToGenerateCsv();
      });
    }
  }, [tableControls]);

  useEffect(() => {
    // When the page mounts, let's fetch new data if the data
    // is stale, that is, if the existing data was fetched
    // BEFORE the component mounted. That indicates the
    // data was fetched in a prior component's lifecycle.
    if (!isFetchedAfterMount && data) {
      setDisplayErrorMessage(false);
      setLoadingState(true);
      refetch();
      refetchStatusToGenerateCsv();
    }
  }, []);

  const { count, schools } = transform(data);
  const [addSchoolRequest] = useAdminConsoleAddSchool();
  const canEdit = useAdminCanMakeChanges();

  useEffect(() => {
    if (window && window.sessionStorage) {
      if (sessionStorage.getItem("schoolUnconfirmedSuccess")) {
        globalToasterRef.current.displayToast(
          SCHOOL_PROFILE_UPDATE_SUCCESS_TOAST_PROPS,
        );
        sessionStorage.removeItem("schoolUnconfirmedSuccess");
      }
    }
  }, []);

  const handleAddSchoolSubmit = async (schoolData) => {
    try {
      const response = await addSchoolRequest(schoolData);
      if (response.error) {
        globalToasterRef.current.displayToast(
          SCHOOL_PROFILE_CREATE_ERROR_TOAST_PROPS,
        );
        return;
      }
      if (response.data) {
        sessionStorage.setItem("schoolCreationSuccess", "true");
        const schoolRecordURL = `/admin/schools/${response.data.id}`;
        router.push(schoolRecordURL);
      }
    } catch {
      globalToasterRef.current.displayToast(
        SCHOOL_PROFILE_CREATE_ERROR_TOAST_PROPS,
      );
    }
  };

  const BREADCRUMB = {
    href: null,
    contents: ADMIN_CONSOLE_ROUTE_CONTENTS.SCHOOLS,
  };

  const FILTER_UI_PROPS = {
    id: "schoolFilters",
    searchFieldLabel: "Search for schools",
    searchFieldPlaceholder: "Search all schools...",
  };

  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);

  useEffect(() => {
    if (userDataFetchFailed || isError) {
      setDisplayErrorMessage(true);
    }
  }, [userDataFetchFailed, isError]);

  return (
    <AdminConsoleBasePage
      title="School directory"
      breadcrumbsSection={BREADCRUMB}
      actions={
        canEdit &&
        userData?.roles?.includes("district_admin") && (
          <AdminConsoleDistrictAddSchoolModal
            onSubmit={handleAddSchoolSubmit}
          />
        )
      }
      showHeaderSeparator
    >
      <AdminConsoleDirectoryTable
        refetchingStatusApiResponse={refetchingStatusApiResponse}
        refetchStatusToGenerateCsv={refetchStatusToGenerateCsv}
        responseFromStatusApi={responseFromStatusApi}
        userData={userData}
        displayErrorMessage={displayErrorMessage && !!loadingState}
        title="School directory"
        data={schools}
        pageSize={PAGE_SIZE}
        totalCount={count}
        columns={COLUMNS}
        onControlsUpdateCb={(controls, rawTableProps) => {
          setTableControls(controls);
          setTableProps(rawTableProps);
        }}
        filterUIProps={FILTER_UI_PROPS}
        loading={loadingState}
        loadingMessage={<div>Loading information...</div>}
        filterDefinitions={{}}
        notFoundMessage={
          <AdminConsoleDirectoryTable.NotFound
            subTitle={NO_SCHOOL_RECORD_FOUND}
            littleTopMargin
          />
        }
        shouldDisplayTotalResultsString
      />
    </AdminConsoleBasePage>
  );
};

export default AdminConsoleSchoolsDirectoryPage;
